class FormSender {
  /**
   * Form Sender
   *
   * Makes a fetch request on the URL supplied as parameter
   * Adds `X-CSRF-TOKEN` header with data from meta tag
   * OAuth can be used by setting the `MIX_APP_AUTH_*` parameters in `.env`
   *
   * @param {FormData} formData Form data to send
   * @param {String} url URL to post data to
   * @method POST
   * @emits FormSender#response { sent: Boolean, error: String }
   */
  constructor(formData, url, corsDomain) {
    this.formData = formData;
    this.url = url;
    this.corsDomain = corsDomain;
    this.listeners = new Map();
    this.useOAuth = true;
    this.checkAuthMode();
  }

  /**
   * Check if OAuth is configured
   */
  checkAuthMode() {
    if (
      process.env.MIX_APP_AUTH_TOKEN_URL === '' ||
      process.env.MIX_APP_AUTH_TOKEN_URL === undefined ||
      process.env.MIX_APP_AUTH_CLIENT_ID === '' ||
      process.env.MIX_APP_AUTH_CLIENT_ID === undefined ||
      process.env.MIX_APP_AUTH_CLIENT_SECRET === '' ||
      process.env.MIX_APP_AUTH_CLIENT_SECRET === undefined
    ) {
      this.useOAuth = false;
    }
  }

  /**
   * Add Listeners
   * @param {String} label
   * @param {Function} callback
   */
  addListener(label, callback) {
    this.listeners.has(label) || this.listeners.set(label, []);
    this.listeners.get(label).push(callback);
  }

  /**
   * Emit Events
   * @param {String} label
   * @param  {...any} args
   */
  emit(label, ...args) {
    const listeners = this.listeners.get(label);
    if (listeners && listeners.length) {
      listeners.forEach(listener => {
        listener(...args);
      });
      return true;
    }
    return false;
  }

  /**
   * Send form or get acces token
   */
  send() {
    if (this.useOAuth) {
      this.getAccessToken();
    } else {
      this.postFormData();
    }
  }

  /**
   * Process server response
   * @param {Response} response
   */
  responseStatus(response) {
    if (response.status >= 200 && response.status < 300) {
      return Promise.resolve(response);
    } else {
      let err = response.statusText !== '' ? `${response.status}: ${response.statusText}` : 'Erreur inconnue.';
      if (response.type === 'opaque') err = 'Problème avec le serveur d’authentification.';
      return Promise.reject(new Error(err));
    }
  }

  /**
   * Get Acces token and post form
   */
  getAccessToken() {
    const url = new URL(process.env.MIX_APP_AUTH_TOKEN_URL);
    const headers = new Headers({
      Accept: '*/*',
    });
    if (process.env.MIX_APP_AUTH_GET_TOKEN === 'headers') {
      headers.append(
        'Authorization',
        `OAuth client_id='${process.env.MIX_APP_AUTH_CLIENT_ID}', client_secret='${process.env.MIX_APP_AUTH_CLIENT_SECRET}', grant_type='authorization_code'`
      );
    } else {
      const params = {
        client_id: process.env.MIX_APP_AUTH_CLIENT_ID,
        client_secret: process.env.MIX_APP_AUTH_CLIENT_SECRET,
        // eslint-disable-next-line quotes
        grant_type: "'authorization_code'",
      };
      Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));
    }
    fetch(url, {
      method: 'POST',
      headers,
    })
      .then(this.responseStatus)
      .then(res => res.json())
      .then(res => {
        this.postFormData(res.access_token);
      })
      .catch(error => {
        this.emit('response', { sent: false, error: error.message });
      });
  }

  /**
   * Get CSRF token
   */
  getCSRFToken() {
    let tokenField = document.querySelector('meta[name="csrf-token"]');
    if (tokenField !== null) {
      return tokenField.getAttribute('content');
    } else {
      tokenField = document.querySelector('input[name="csrf-token"]');
      return tokenField.value;
    }
  }

  /**
   * Post form data
   * @param {String} access_token
   */
  postFormData(access_token = null) {
    const url = new URL(this.url);
    const headers = new Headers({
      Accept: '*/*',
      'X-CSRF-TOKEN': this.getCSRFToken(),
      'Access-Control-Allow-Origin': `${window.location.protocol}//${window.location.host}`,
    });
    if (this.corsDomain !== undefined) {
      headers.append('Access-Control-Allow-Origin', this.corsDomain);
    }
    if (this.useOAuth) {
      if (process.env.MIX_APP_AUTH_POST_FORMDATA === 'headers') {
        headers.append('Authorization', `Bearer ${access_token}`);
      } else {
        const params = {
          Bearer: access_token,
        };
        Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));
      }
    }
    fetch(url, {
      method: 'POST',
      headers,
      body: this.formData,
    })
      .then(this.responseStatus)
      .then(res => res.json())
      .then(res => {
        this.emit('response', { sent: true, data: res });
      })
      .catch(error => {
        this.emit('response', { sent: false, error: error.message });
      });
  }
}

export default FormSender;



// WEBPACK FOOTER //
// ./scripts/util/FormSender.js